'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { X, QrCode, Smartphone, CheckCircle2, RefreshCw, AlertCircle, Wifi } from 'lucide-react';

interface WhatsAppQrModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConnected: (phone: string) => Promise<void> | void;
}

export function WhatsAppQrModal({ isOpen, onClose, onConnected }: WhatsAppQrModalProps) {
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [instanceName, setInstanceName] = useState('');
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'DISCONNECTED' | 'WAITING_QR' | 'CONNECTING' | 'CONNECTED'>('DISCONNECTED');
  const [connectedPhone, setConnectedPhone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Inicializar o solicitar instancia y QR al abrir el modal
  const initInstance = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/whatsapp/instance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'create' })
      });
      const data = await res.json();
      if (data.success) {
        setInstanceName(data.instanceName || '');
        if (data.qrCode) setQrCode(data.qrCode);
        setConnectionStatus(data.status || 'WAITING_QR');
      } else {
        setError(data.error || 'No se pudo inicializar la instancia de WhatsApp');
      }
    } catch (err: any) {
      setError(err.message || 'Error de conexión');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      initInstance();
    } else {
      setQrCode(null);
      setConnectionStatus('DISCONNECTED');
      setError(null);
    }
  }, [isOpen, initInstance]);

  // Polling del estado de conexión mientras el modal esté abierto y en espera
  useEffect(() => {
    if (!isOpen || connectionStatus === 'CONNECTED') return;

    const interval = setInterval(async () => {
      try {
        const res = await fetch('/api/whatsapp/instance', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'status' })
        });
        const data = await res.json();
        if (data.success && data.status) {
          const st = data.status.status;
          setConnectionStatus(st);
          if (data.status.qrCode && !qrCode) {
            setQrCode(data.status.qrCode);
          }
          if (st === 'CONNECTED') {
            const phone = data.status.phone || '+54 9 11 3176-7548';
            setConnectedPhone(phone);
            onConnected(phone);
            setTimeout(() => {
              onClose();
            }, 2000);
          }
        }
      } catch (err) {
        // Silencioso en polling
      }
    }, 2500);

    return () => clearInterval(interval);
  }, [isOpen, connectionStatus, qrCode, onConnected, onClose]);

  const handleRefreshQr = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const res = await fetch('/api/whatsapp/instance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'refresh_qr' })
      });
      const data = await res.json();
      if (data.success && data.qrCode) {
        setQrCode(data.qrCode);
        setConnectionStatus('WAITING_QR');
      } else {
        setError(data.error || 'No se pudo regenerar el QR');
      }
    } catch (err: any) {
      setError(err.message || 'Error de red');
    } finally {
      setRefreshing(false);
    }
  };

  // Simulación de prueba técnica controlada para validación previa al despliegue
  const handleSimulatePairing = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/whatsapp/instance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'simulate_pairing',
          phone: '+54 9 11 3176-7548'
        })
      });
      const data = await res.json();
      if (data.success) {
        setConnectionStatus('CONNECTED');
        setConnectedPhone(data.phone);
        await onConnected(data.phone);
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-xs transition-opacity animate-in fade-in">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden border border-slate-100 flex flex-col">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-center justify-between border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-emerald-50 text-emerald-700 rounded-xl">
              <QrCode className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">Vincular WhatsApp</h3>
              <p className="text-xs text-slate-500">
                {instanceName ? `Instancia: ${instanceName}` : 'Conexión oficial Baileys'}
              </p>
            </div>
          </div>
          <button
            type="button"
            id="btn-close-qr-modal"
            onClick={onClose}
            className="p-1.5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5 text-center">
          {error && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-2xl flex items-center gap-2 text-rose-700 text-xs text-left">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* QR Code Container */}
          <div className="w-60 h-60 mx-auto bg-slate-50 border-2 border-slate-200 rounded-3xl p-4 flex flex-col items-center justify-center relative shadow-inner overflow-hidden">
            {connectionStatus === 'CONNECTED' ? (
              <div className="flex flex-col items-center justify-center text-emerald-600 space-y-2 animate-in zoom-in">
                <CheckCircle2 className="w-16 h-16 stroke-[2.5]" />
                <p className="text-sm font-bold">¡WhatsApp Vinculado!</p>
                <p className="text-xs text-slate-600">{connectedPhone || 'Sesión activa'}</p>
              </div>
            ) : qrCode ? (
              <div className="w-full h-full flex flex-col items-center justify-center">
                <img
                  id="whatsapp-qr-img"
                  src={qrCode.startsWith('data:') ? qrCode : `data:image/png;base64,${qrCode}`}
                  alt="QR Code WhatsApp"
                  className="w-full h-full object-contain mix-blend-multiply"
                />
              </div>
            ) : connectionStatus === 'CONNECTING' ? (
              <div className="flex flex-col items-center justify-center text-emerald-700 space-y-3">
                <RefreshCw className="w-12 h-12 animate-spin text-emerald-600" />
                <p className="text-xs font-bold animate-pulse">Conectando sesión con WhatsApp...</p>
                <p className="text-[11px] text-slate-500">Sincronizando claves criptográficas</p>
              </div>
            ) : loading ? (
              <div className="flex flex-col items-center justify-center text-slate-400 space-y-2">
                <RefreshCw className="w-8 h-8 animate-spin" />
                <p className="text-xs font-semibold">Generando código QR...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center text-slate-400 space-y-2">
                <Smartphone className="w-10 h-10 stroke-1" />
                <p className="text-xs font-medium text-slate-500">Esperando código QR...</p>
                <button
                  type="button"
                  id="btn-retry-qr"
                  onClick={handleRefreshQr}
                  className="mt-2 text-xs text-emerald-700 font-bold hover:underline flex items-center gap-1"
                >
                  <RefreshCw className="w-3 h-3" />
                  <span>Reintentar</span>
                </button>
              </div>
            )}
          </div>

          <div className="space-y-1">
            <p className="text-xs font-bold text-slate-800 flex items-center justify-center gap-1.5">
              <Wifi className="w-3.5 h-3.5 text-emerald-600" />
              <span>Instrucciones de vinculación:</span>
            </p>
            <p className="text-[11px] text-slate-500 leading-relaxed max-w-xs mx-auto">
              1. Abrí WhatsApp en tu celular.<br />
              2. Tocá <strong>Menú (⋮)</strong> o <strong>Configuración</strong> → <strong>Dispositivos vinculados</strong>.<br />
              3. Tocá <strong>Vincular un dispositivo</strong> y apuntá al código QR.
            </p>
          </div>

          {/* Acciones y control de estado */}
          <div className="pt-2 flex flex-col gap-2">
            <button
              type="button"
              id="btn-refresh-qr"
              disabled={refreshing || loading || connectionStatus === 'CONNECTED'}
              onClick={handleRefreshQr}
              className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-slate-700 font-semibold text-xs rounded-xl transition flex items-center justify-center gap-2 cursor-pointer"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              <span>Actualizar código QR</span>
            </button>

            {/* Opción de prueba previa cuando no se tenga teléfono a mano */}
            <button
              type="button"
              onClick={handleSimulatePairing}
              disabled={loading || connectionStatus === 'CONNECTED'}
              className="text-[11px] text-slate-400 hover:text-emerald-700 transition underline py-1"
            >
              Simular escaneo de prueba (+54 9 11 3176-7548)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
